import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, MapPin, Phone, Globe, Clock, Star, ExternalLink, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useStructuredData } from '../hooks/useStructuredData';
import { generateLocalBusinessSchema } from '../utils/structuredData';
import { DiscountSection } from './DiscountDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BusinessPage = ({ onBackToHome }) => {
  const { businessId } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  // Generate structured data for the business
  const businessSchema = React.useMemo(() => {
    return business ? generateLocalBusinessSchema(business) : null;
  }, [business]);

  // Apply structured data
  useStructuredData(businessSchema, 'business-schema');

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const response = await axios.get(`${API}/businesses/${businessId}`);
        setBusiness(response.data);
      } catch (error) {
        console.error('Error fetching business:', error);
        setBusiness(null);
      } finally {
        setLoading(false);
      }
    };

    if (businessId) {
      fetchBusiness();
    }
  }, [businessId]);

  const getTierBadgeColor = (tier) => {
    const colors = {
      'elite': 'bg-purple-100 text-purple-800',
      'platinum': 'bg-gray-300 text-gray-800',
      'deluxe': 'bg-yellow-100 text-yellow-800',
      'standard': 'bg-blue-100 text-blue-800'
    };
    return colors[tier?.toLowerCase()] || colors.standard;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading business details...</p>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-charcoal mb-4">Business Not Found</h1>
          <p className="text-gray-600 mb-6">The business you're looking for doesn't exist or has been removed.</p>
          <Button onClick={onBackToHome}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Directory
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <Button 
            onClick={onBackToHome}
            variant="outline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Directory
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Business Info */}
          <div className="lg:col-span-2">
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-3xl font-bold text-charcoal mb-2">
                      {business.name}
                    </CardTitle>
                    <div className="flex items-center gap-2 mb-4">
                      <Badge className={getTierBadgeColor(business.tier)}>
                        {business.tier?.toUpperCase() || 'STANDARD'}
                      </Badge>
                      {business.is_verified && (
                        <Badge className="bg-green-100 text-green-800">
                          ✓ Verified
                        </Badge>
                      )}
                    </div>
                  </div>
                  {business.logo_url && (
                    <img 
                      src={business.logo_url} 
                      alt={`${business.name} logo`}
                      className="w-20 h-20 object-contain rounded-lg"
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  {business.description}
                </p>

                {/* Dynamic Discount Section */}
                <DiscountSection business={business} />

                {/* Current Offers */}
                {business.current_offers && business.current_offers.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-charcoal mb-3">
                      Current Offers
                    </h3>
                    <div className="space-y-2">
                      {business.current_offers.map((offer, index) => (
                        <div key={index} className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                          <Star className="h-4 w-4 text-green-600" />
                          <span className="text-green-800 font-medium">{offer}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* QR Code & Chatbot Access */}
                <div className="bg-primary/5 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-charcoal mb-3">
                    Get Live Discounts
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Scan the QR code in-store or visit our chatbot for instant, personalized discounts!
                  </p>
                  <Button 
                    className="w-full sm:w-auto"
                    onClick={() => window.open(`https://${business.chatbotUrl}`, '_blank')}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Access Live Discounts
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Business Details Sidebar */}
          <div className="lg:col-span-1">
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Business Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {business.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-charcoal">Address</h4>
                      <p className="text-gray-600 text-sm">{business.address}</p>
                    </div>
                  </div>
                )}

                {business.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-charcoal">Phone</h4>
                      <a 
                        href={`tel:${business.phone}`}
                        className="text-primary hover:text-orange text-sm"
                      >
                        {business.phone}
                      </a>
                    </div>
                  </div>
                )}

                {business.website && (
                  <div className="flex items-start gap-3">
                    <Globe className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-charcoal">Website</h4>
                      <a 
                        href={business.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-orange text-sm flex items-center gap-1"
                      >
                        Visit Website <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                )}

                {business.opening_hours && (
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h4 className="font-medium text-charcoal">Opening Hours</h4>
                      <p className="text-gray-600 text-sm">{business.opening_hours}</p>
                    </div>
                  </div>
                )}

                {business.category && (
                  <div>
                    <h4 className="font-medium text-charcoal mb-2">Category</h4>
                    <Badge variant="outline">{business.category}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Additional Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.open(`https://${business.chatbotUrl}`, '_blank')}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Chat for Discounts
                </Button>
                
                {business.website && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open(business.website, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Visit Website
                  </Button>
                )}

                {business.phone && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open(`tel:${business.phone}`)}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Call Now
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessPage;